import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Badge } from '../../../foundation/altzero_base/base/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../foundation/altzero_base/base/components/ui/select';

import {
  Star,
  Calendar,
  Zap,
  DollarSign,
  Leaf,
  Building,

  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff
} from 'lucide-react';
import { useDocumentForm } from '../contexts/DocumentFormContext';
import { Offer, ENERGY_RETAILERS, CONTRACT_LENGTHS, createEmptyEnvironmentalCharges, createEmptyNonEnergyCharges, EnvironmentalCharges } from '../types/offerTypes';
import { getActiveRetailers, type Retailer } from '../services/retailerService';
import { getOffers } from '../services/tenderApiService';
import { WorkflowNavigation } from './WorkflowNavigation';
import { normalizeEnvironmentalCharges, normalizeNonEnergyCharges } from '../lib/normalizeCharges';
import { formatTermLabel, formatRateYearLabel } from '../lib/termLabel';
import { formatEnergyRate } from '../lib/ratePrecision';
import { maxPricedYear } from '../lib/pricedYears';
import { applyRankedSelectionsToOffers, isSharedAbnTender } from '../lib/recommendedFromSelections';
import type { RankedOfferSelection } from '../types/offerTypes';

interface RecommendedOfferStepProps {
  onNext: () => void;
  onPrevious: () => void;
}

const RecommendedOfferStep: React.FC<RecommendedOfferStepProps> = ({ onNext, onPrevious }) => {
  const { clientData, offers: contextOffers, setOffers, sites, tenderId } = useDocumentForm();

  // LOCAL STATE FOR OFFERS - Persists across navigation
  const [localOffers, setLocalOffers] = useState<Offer[]>([]);
  const hasLoadedRef = useRef(false); // Track if we've attempted to load
  const lastTenderIdRef = useRef<string | null>(null); // Track tenderId to reset on change

  const [expandedOffers, setExpandedOffers] = useState<Set<string>>(new Set());
  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [loadingRetailers, setLoadingRetailers] = useState(true);
  const [isLoadingOffers, setIsLoadingOffers] = useState(true);
  const [selectedContractTerm, setSelectedContractTerm] = useState<number | null>(null); // NEW: Contract term filter

  // Check if context offers have recommended offers
  const contextHasRecommendedOffers = contextOffers.some(offer =>
    offer.recommendedMonths && offer.recommendedMonths.length > 0
  );

  // Use local offers if available, otherwise context offers
  // CRITICAL: Show ALL offers, recommended ones will be highlighted in UI
  const getDisplayOffers = (): Offer[] => {
    if (localOffers.length > 0) {
      // Show ALL local offers (recommended will be highlighted)
      return localOffers;
    }

    if (contextOffers.length > 0) {
      // Show ALL context offers (recommended will be highlighted)
      return contextOffers;
    }

    return [];
  };

  /*
   * The reviewer's 1st Choice, read from the store Compare & Select actually maintains.
   *
   * ⚠️ WITHOUT THIS THE STEP DEAD-ENDS ON A TENDER WHOSE PICKS ARE ALL THERE. The per-offer
   * `recommended_months` mirror is written inside the rank-1 click handler and can silently not
   * happen; this step gates on it, so it printed "No Recommended Offers — go back to Step 2"
   * while Step 2 showed every pick selected. See applyRankedSelectionsToOffers.
   */
  const [rankedSelections, setRankedSelections] = useState<RankedOfferSelection[]>([]);
  useEffect(() => {
    if (!tenderId) return;
    let cancelled = false;
    const load = async () => {
      const { getRankedOfferSelections } = await import('../services/tenderApiService');
      const result = await getRankedOfferSelections(tenderId);
      if (!cancelled && result.success && result.selections) setRankedSelections(result.selections);
    };
    load();
    return () => { cancelled = true; };
  }, [tenderId]);

  // Memoised so the hydrated list keeps a stable identity. Without it this array is new on every
  // render and the auto-expand effect below, which lists `offers` as a dependency, re-runs each
  // time — harmless (its own guard stops it acting twice) but pure waste.
  const offers = useMemo(
    () => applyRankedSelectionsToOffers(
      getDisplayOffers(),
      rankedSelections,
      isSharedAbnTender(sites),
    ) as Offer[],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getDisplayOffers reads exactly these
    [localOffers, contextOffers, rankedSelections, sites],
  );

  // Helper function to map offers with correct siteIds (memoized)
  const mapOffersWithSites = useCallback((offersToMap: Offer[]): Offer[] => {
    if (sites.length === 0) {
      console.warn('⚠️ RecommendedOfferStep: Cannot map offers - sites not loaded yet');
      return offersToMap; // Return as-is if sites not available
    }

    return offersToMap.map((dbOffer: any) => {
      // dbOffer.siteId is the database site_id from tender_site
      // We need to match it with frontend site.id

      // Strategy 1: Try to match database site_id with frontend site.id (if they're the same)
      const matchingSiteById = sites.find(s => s.id === dbOffer.siteId);
      if (matchingSiteById) {
        return {
          ...dbOffer,
          siteId: matchingSiteById.id,
          siteName: matchingSiteById.name || dbOffer.siteName,
        };
      }

      // Strategy 2: Try to match by site name (fallback)
      const matchingSiteByName = sites.find(s => s.name === dbOffer.siteName);
      if (matchingSiteByName) {
        console.log(`📌 RecommendedOfferStep: Matched offer site by name: ${dbOffer.siteName} -> ${matchingSiteByName.id}`);
        return {
          ...dbOffer,
          siteId: matchingSiteByName.id,
          siteName: matchingSiteByName.name,
        };
      }

      // Strategy 3: If no match, keep original but log warning
      console.warn(`⚠️ RecommendedOfferStep: Could not match site for offer ${dbOffer.id} (siteId: ${dbOffer.siteId}, siteName: ${dbOffer.siteName})`);
      return dbOffer;
    });
  }, [sites]);

  // Reset hasLoadedRef when tenderId changes
  useEffect(() => {
    if (tenderId !== lastTenderIdRef.current) {
      hasLoadedRef.current = false;
      lastTenderIdRef.current = tenderId;
    }
  }, [tenderId]);

  // CRITICAL: Load offers from DB - ONLY when sites are available AND context doesn't have recommended offers
  useEffect(() => {
    const loadOffersFromDatabase = async () => {
      // CRITICAL: If context already has recommended offers, use them - don't reload
      if (contextHasRecommendedOffers && contextOffers.length > 0) {
        console.log('✅ RecommendedOfferStep: Using recommended offers from context');
        // Map context offers with sites if needed
        if (sites.length > 0) {
          const mappedOffers = mapOffersWithSites(contextOffers);
          setLocalOffers(mappedOffers);
          // Don't overwrite context if it already has the right data
        } else {
          setLocalOffers(contextOffers);
        }
        setIsLoadingOffers(false);
        return;
      }

      // CRITICAL: Wait for sites to be loaded before attempting to load offers
      if (sites.length === 0) {
        console.log('⏳ RecommendedOfferStep: Waiting for sites to load...');
        return; // stay in loading state — sites will trigger re-run
      }

      // Only load if tenderId exists
      if (!tenderId) {
        setIsLoadingOffers(false);
        return;
      }

      // Only load once per tenderId
      if (hasLoadedRef.current) {
        console.log('⏭️ RecommendedOfferStep: Already loaded, skipping');
        setIsLoadingOffers(false);
        return;
      }

      try {
        setIsLoadingOffers(true);
        hasLoadedRef.current = true;
        console.log('🔄 RecommendedOfferStep: Loading offers from DB');
        const result = await getOffers(tenderId);

        if (result.success && result.offers && result.offers.length > 0) {
          // Map offers with correct siteIds now that sites are available
          const mappedOffers = mapOffersWithSites(result.offers);

          // Load ALL offers (recommended will be highlighted in UI)
          const recommendedCount = mappedOffers.filter(offer =>
            offer.recommendedMonths && offer.recommendedMonths.length > 0
          ).length;

          console.log(`✅ RecommendedOfferStep: Loaded ${mappedOffers.length} offers from DB, ${recommendedCount} recommended`);

          if (mappedOffers.length > 0) {
            setLocalOffers(mappedOffers); // Store ALL offers in LOCAL STATE
            setOffers(mappedOffers); // Also update context with ALL offers
          } else if (contextOffers.length > 0) {
            // If no offers in DB, use context offers
            const mappedContext = mapOffersWithSites(contextOffers);
            setLocalOffers(mappedContext);
            setOffers(mappedContext);
          }
        }
      } catch (error) {
        console.error('❌ RecommendedOfferStep: Error loading offers:', error);
        hasLoadedRef.current = false; // Reset on error so we can retry
      } finally {
        setIsLoadingOffers(false);
      }
    };

    loadOffersFromDatabase();
  }, [tenderId, sites.length, setOffers, mapOffersWithSites, contextOffers, contextHasRecommendedOffers]); // Include contextOffers in deps

  // CRITICAL: Re-map offers when sites become available (if offers already loaded)
  useEffect(() => {
    // Use ALL context offers if sites are available and local offers are empty
    if (contextOffers.length > 0 && sites.length > 0 && localOffers.length === 0) {
      const mappedOffers = mapOffersWithSites(contextOffers);
      setLocalOffers(mappedOffers);
      setOffers(mappedOffers);
      return;
    }

    // Only re-map if we have offers but sites just became available
    if (localOffers.length > 0 && sites.length > 0) {
      // Check if any offers have unmapped siteIds
      const needsRemapping = localOffers.some(offer => {
        const matchingSite = sites.find(s => s.id === offer.siteId);
        return !matchingSite; // If site not found, needs remapping
      });

      if (needsRemapping) {
        console.log('🔄 RecommendedOfferStep: Re-mapping offers with newly loaded sites');
        const remappedOffers = mapOffersWithSites(localOffers);
        setLocalOffers(remappedOffers);
        setOffers(remappedOffers);
      }
    }
  }, [sites.length, localOffers, setOffers, mapOffersWithSites, contextOffers, contextHasRecommendedOffers]); // Re-run when sites load

  // Fetch retailers from database
  useEffect(() => {
    const fetchRetailers = async () => {
      try {
        setLoadingRetailers(true);
        const result = await getActiveRetailers();
        if (result.success && result.data) {
          setRetailers(result.data);
          console.log('✅ Fetched retailers for RecommendedOfferStep:', result.data);
        } else {
          console.error('❌ Failed to fetch retailers:', result.error);
        }
      } catch (error) {
        console.error('❌ Error fetching retailers:', error);
      } finally {
        setLoadingRetailers(false);
      }
    };
    fetchRetailers();
  }, []);

  // Auto-expand first site with offers
  useEffect(() => {
    const sitesWithOffers = getOffersBySite();
    if (sitesWithOffers.length > 0 && expandedSites.size === 0) {
      setExpandedSites(new Set([sitesWithOffers[0].site.id]));
    }
  }, [offers, sites, expandedSites.size]);

  // Helper function to format recommended months
  const formatRecommendedMonths = (offer: Offer): string => {
    if (offer.recommendedMonths && offer.recommendedMonths.length > 0) {
      const term = offer.recommendedMonths[0];
      return formatTermLabel(term);
    }

    // Fallback to first available term
    if (offer.availableTerms && offer.availableTerms.length > 0) {
      return formatTermLabel(offer.availableTerms[0]);
    }

    return 'N/A';
  };

  // Get ALL offers for a specific contract term - recommended will be highlighted in UI
  const getOffersForMonth = (months: number): Offer[] => {
    const filteredOffers = offers.filter(offer => {
      // Show all offers matching this term (recommended will be highlighted)
      const matchesRequestedTerm = offer.availableTerms?.includes(months) ?? false;
      // If contract term filter is selected, only show offers matching that filter
      const matchesContractTermFilter = !selectedContractTerm || (offer.availableTerms?.includes(selectedContractTerm) ?? false);
      return matchesRequestedTerm && matchesContractTermFilter;
    });
    return filteredOffers;
  };

  // Group offers by site — only include the recommended offer(s)
  const getOffersBySite = () => {
    const siteOffers = new Map<string, { site: any; offers: Offer[] }>();

    // Initialize with all sites
    sites.forEach(site => {
      siteOffers.set(site.id, { site, offers: [] });
    });

    offers.forEach(offer => {
      // Only include offers that are recommended
      const isRecommended = offer.recommendedMonths && offer.recommendedMonths.length > 0;
      if (!isRecommended) return;

      // Filter by contract term dropdown if selected
      if (selectedContractTerm && !(offer.recommendedMonths?.includes(selectedContractTerm) ?? false)) {
        return;
      }

      const siteId = offer.siteId || offer.tenderSiteId;
      if (siteId && siteOffers.has(siteId)) {
        siteOffers.get(siteId)!.offers.push(offer);
      } else {
        const unknownSiteId = 'unknown-site';
        if (!siteOffers.has(unknownSiteId)) {
          siteOffers.set(unknownSiteId, {
            site: { id: unknownSiteId, name: 'Other Offers', nmi: '' },
            offers: []
          });
        }
        siteOffers.get(unknownSiteId)!.offers.push(offer);
      }
    });

    return Array.from(siteOffers.values()).filter(({ offers }) => offers.length > 0);
  };

  // Get offers for a specific site and contract length — only the recommended offer for that term
  const getSiteOffersForMonth = (siteId: string, months: number): Offer[] => {
    const siteData = getOffersBySite().find(({ site }) => site.id === siteId);
    if (!siteData) return [];

    return siteData.offers.filter(offer =>
      offer.recommendedMonths?.includes(months) ?? false
    );
  };

  // Toggle site expansion
  const toggleSiteExpansion = (siteId: string) => {
    setExpandedSites(prev => {
      const newSet = new Set(prev);
      if (newSet.has(siteId)) {
        newSet.delete(siteId);
      } else {
        newSet.add(siteId);
      }
      return newSet;
    });
  };

  // Check if we have any recommended offers - Dynamic check
  const hasRecommendedOffers = offers.some(offer =>
    offer.recommendedMonths && offer.recommendedMonths.length > 0
  );

  // Get available contract terms from ALL offers (for filter dropdown)
  const getAvailableContractTerms = (): number[] => {
    const terms = new Set<number>();
    offers.forEach(offer => {
      // Get all available terms from this offer
      if (offer.availableTerms && Array.isArray(offer.availableTerms)) {
        offer.availableTerms.forEach(term => terms.add(Number(term)));
      }
    });
    return Array.from(terms).sort((a, b) => a - b);
  };

  // Get ONLY recommended contract terms (for display)
  const getRecommendedContractTerms = (): number[] => {
    const terms = new Set<number>();
    offers.forEach(offer => {
      // Get only recommended months from this offer
      if (offer.recommendedMonths && Array.isArray(offer.recommendedMonths)) {
        offer.recommendedMonths.forEach(term => terms.add(Number(term)));
      }
    });
    return Array.from(terms).sort((a, b) => a - b);
  };

  const isReadyForNext = () => {
    return hasRecommendedOffers;
  };

  // Get site statistics — only count the recommended offer and its recommended terms
  const getSiteStats = (siteId: string) => {
    const siteData = getOffersBySite().find(({ site }) => site.id === siteId);
    if (!siteData) return { totalOffers: 0, contractLengths: [] };

    // Only count recommended terms from recommended offers
    const uniqueMonths = new Set<number>();
    siteData.offers.forEach(offer => {
      if (offer.recommendedMonths && Array.isArray(offer.recommendedMonths)) {
        offer.recommendedMonths.forEach(term => uniqueMonths.add(Number(term)));
      }
    });

    return {
      totalOffers: siteData.offers.length,
      contractLengths: Array.from(uniqueMonths).sort((a, b) => a - b)
    };
  };

  // Toggle offer expansion
  const toggleOfferExpansion = (offerId: string) => {
    const newExpanded = new Set(expandedOffers);
    if (newExpanded.has(offerId)) {
      newExpanded.delete(offerId);
    } else {
      newExpanded.add(offerId);
    }
    setExpandedOffers(newExpanded);
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Format rate (c/kWh)
  const formatRate = (rate: number) => {
    // Full quoted precision — see lib/ratePrecision; 9.9076 must not print as 9.91.
    return `${formatEnergyRate(rate)}c/kWh`;
  };

  // Render detailed offer information
  const renderOfferDetails = (offer: Offer, yearsToDisplay: string[], currentTabMonths: number) => {
    // First try to find retailer in database retailers, then fallback to hardcoded ones
    const retailer = retailers.find(r => r.id === offer.retailerId) ||
      ENERGY_RETAILERS.find(r => r.id === offer.retailerId);

    // Extract rate data for the specific contract term from contract_terms
    const contractTermData = offer.multiYearRateData?.contract_terms?.[currentTabMonths.toString()];

    // Transform contract term data into the format expected by the UI
    const rateData = {
      peakRates: {} as any,
      offpeakRates: {} as any,
      shoulderRates: {} as any,
      // No defaults passed — this offer's rates were already resolved once, server-side, by
      // offerAutoPopulateService. Re-deriving here would silently overwrite a deliberate
      // "leave this blank" review decision with a freshly computed number.
      environmentalCharges: normalizeEnvironmentalCharges(offer.multiYearRateData?.smooth?.environmentalCharges as any) || createEmptyEnvironmentalCharges(),
      nonEnergyCharges: normalizeNonEnergyCharges(offer.multiYearRateData?.smooth?.nonEnergyCharges || {})
    };

    // Source per-year rate object: prefer this term's contract_terms data, else the smooth variant.
    const smoothSrc = offer.multiYearRateData?.smooth;
    const sourceYear = (yearKey: string): any =>
      contractTermData?.[yearKey] ?? (smoothSrc
        ? { peak: smoothSrc.peakRates?.[yearKey], offpeak: smoothSrc.offpeakRates?.[yearKey], shoulder: smoothSrc.shoulderRates?.[yearKey] }
        : {});
    // Fill EVERY contract year, carrying the last known value forward so the final review
    // never hides a contract year. Smooth offers store one blended rate; some offers don't
    // populate every year — without this a 4-year contract can show only 3 years. See #27/#41/#56.
    let lastPeak = 0, lastOffpeak = 0, lastShoulder = 0;
    yearsToDisplay.forEach((yearKey) => {
      const yd: any = sourceYear(yearKey) || {};
      lastPeak = yd.peak || lastPeak;
      lastOffpeak = yd.offpeak || lastOffpeak;
      lastShoulder = yd.shoulder || lastShoulder;
      rateData.peakRates[yearKey] = lastPeak;
      rateData.offpeakRates[yearKey] = lastOffpeak;
      rateData.shoulderRates[yearKey] = lastShoulder;
    });

    // Explicitly cast environmentalCharges to ensure TS knows it's the full type
    const envCharges = rateData.environmentalCharges as EnvironmentalCharges;

    // Carry forward each scheme's per-year ratePerKwh so every contract year shows an env
    // rate instead of being hidden — smooth offers repeat the same env charge each year. #27
    const ENV_SCHEMES = ['LREC', 'VEEC/ESS/REES', 'SREC'] as const;
    const envByYear: Record<string, Record<string, number>> = {};
    {
      const lastSeen: Record<string, number> = {};
      for (const year of yearsToDisplay) {
        const yk = year as keyof typeof envCharges.LREC;
        envByYear[year] = {};
        for (const scheme of ENV_SCHEMES) {
          const v = (envCharges as any)[scheme]?.[yk]?.ratePerKwh;
          if (typeof v === 'number' && v > 0) lastSeen[scheme] = v;
          envByYear[year][scheme] = lastSeen[scheme] ?? 0;
        }
      }
    }
    const hasAnyEnv = ENV_SCHEMES.some(s => yearsToDisplay.some(y => envByYear[y][s] > 0));

    console.log("🔍 RecommendedOfferStep: renderOfferDetails", {
      offerId: offer.id,
      retailerName: offer.retailerName,
      currentTabMonths: currentTabMonths,
      availableTerms: offer.availableTerms,
      contractTermDisplay: formatTermLabel(currentTabMonths),
      hasMultiYearRateData: !!offer.multiYearRateData,
      yearsToDisplay: yearsToDisplay,
      rateData: rateData,
      peakRatesCount: Object.keys(rateData.peakRates).length,
      offpeakRatesCount: Object.keys(rateData.offpeakRates).length,
      environmentalChargesCount: Object.keys(rateData.environmentalCharges).length,
      nonEnergyChargesCount: Object.keys(rateData.nonEnergyCharges).length,
      peakRatesForYears: yearsToDisplay.map(year => ({ year, rate: rateData.peakRates[year] })),
      offpeakRatesForYears: yearsToDisplay.map(year => ({ year, rate: rateData.offpeakRates[year] }))
    });

    return (
      <div className="mt-4 space-y-4 border-t pt-4">
        {/* Multi-Year Rates Table */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-blue-600" />
            Multi-Year Energy Rates
          </h4>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Peak Rates */}
              <div className="space-y-2">
                <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">Peak Rates</div>
                {yearsToDisplay.some(year => rateData.peakRates[year] > 0) ? (
                  yearsToDisplay
                    .filter(year => rateData.peakRates[year] > 0)
                    .map((year) => (
                      <div key={year} className="flex justify-between text-sm">
                        <span className="text-gray-700">{formatRateYearLabel(year)}</span>
                        <span className="font-medium">{formatRate(rateData.peakRates[year])}</span>
                      </div>
                    ))
                ) : (
                  <div className="text-sm text-gray-500 italic">No peak rates configured</div>
                )}
              </div>

              {/* Off-Peak Rates */}
              <div className="space-y-2">
                <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">Off-Peak Rates</div>
                {yearsToDisplay.some(year => rateData.offpeakRates[year] > 0) ? (
                  yearsToDisplay
                    .filter(year => rateData.offpeakRates[year] > 0)
                    .map((year) => (
                      <div key={year} className="flex justify-between text-sm">
                        <span className="text-gray-700">{formatRateYearLabel(year)}</span>
                        <span className="font-medium">{formatRate(rateData.offpeakRates[year])}</span>
                      </div>
                    ))
                ) : (
                  <div className="text-sm text-gray-500 italic">No off-peak rates configured</div>
                )}
              </div>

              {/* Shoulder Rates */}
              <div className="space-y-2">
                <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">Shoulder Rates</div>
                {yearsToDisplay.some(year => rateData.shoulderRates[year] > 0) ? (
                  yearsToDisplay
                    .filter(year => rateData.shoulderRates[year] > 0)
                    .map((year) => (
                      <div key={year} className="flex justify-between text-sm">
                        <span className="text-gray-700">{formatRateYearLabel(year)}</span>
                        <span className="font-medium">{formatRate(rateData.shoulderRates[year])}</span>
                      </div>
                    ))
                ) : (
                  <div className="text-sm text-gray-500 italic">No shoulder rates configured</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Environmental Charges */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Leaf className="w-4 h-4 text-green-600" />
            Environmental Charges
          </h4>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="space-y-3">
              {/* Show environmental charges for ALL contract years (carry-forward filled). #27 */}
              {hasAnyEnv && yearsToDisplay.map((year) => (
                <div key={year} className="border-l-4 border-green-500 pl-4">
                  <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">
                    Year {year.replace('year_', '')} Environmental Charges
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    {ENV_SCHEMES.map((scheme) => envByYear[year][scheme] > 0 ? (
                      <div key={scheme} className="text-center">
                        <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">{scheme}</div>
                        <div className="font-medium">{formatRate(envByYear[year][scheme])}</div>
                      </div>
                    ) : null)}
                  </div>
                </div>
              ))}
              {!hasAnyEnv && (
                <div className="text-sm text-gray-500 italic">No environmental charges configured</div>
              )}
            </div>
          </div>
        </div>

        {/* Non-Energy Charges */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-purple-600" />
            Additional Charges
          </h4>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {Object.entries(rateData.nonEnergyCharges).map(([chargeName, value]) => (
                <div key={chargeName} className="flex justify-between">
                  <span className="text-gray-700">{chargeName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
                  <span className="font-medium">{formatCurrency(value as number)}/year</span>
                </div>
              ))}
              {Object.keys(rateData.nonEnergyCharges).length === 0 && (
                <div className="text-sm text-gray-500 italic col-span-2">No additional charges configured</div>
              )}
            </div>
          </div>
        </div>

        {/* Contract Summary */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Building className="w-4 h-4 text-gray-600" />
            Contract Summary
          </h4>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Contract Term</div>
                <div className="font-medium text-gray-900">{formatTermLabel(currentTabMonths)}</div>
              </div>
              <div className="text-center">
                <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Green Energy</div>
                <div className="font-medium">{offer.greenPercent}%</div>
              </div>
              <div className="text-center">
                <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Est. Annual Cost</div>
                <div className="font-medium">{offer.estimatedAnnualCost ? formatCurrency(offer.estimatedAnnualCost) : 'TBD'}</div>
              </div>
              <div className="text-center">
                <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Est. Savings</div>
                <div className="font-medium text-green-600">{offer.estimatedSavingsPercent ? `${offer.estimatedSavingsPercent}%` : 'TBD'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Year slots to display for a contract length, capped at what the offer actually prices.
  //
  // ceil(contractMonths / 12) alone showed a year a reviewer had deleted in the parsing step:
  // the deletion persisted, but the environmental shape these cards read is deep-merged against
  // createEmptyEnvironmentalCharges() (year_1 … year_6 for every scheme), so the slot came back
  // as zeros and rendered an empty row. Passing the offer lets the row count come from the data.
  const getYearsToDisplay = (contractMonths: number, offer?: Offer) => {
    const nominal = Math.max(1, Math.ceil(contractMonths / 12));
    const smooth = (offer?.multiYearRateData as any)?.smooth;
    const priced = offer
      ? Math.max(
        maxPricedYear(smooth?.environmentalCharges),
        maxPricedYear((offer.extn as any)?.environmentalCharges),
        maxPricedYear((offer.extn as any)?.environmental_charges),
        maxPricedYear(smooth?.peakRates),
        maxPricedYear(smooth?.offpeakRates),
        maxPricedYear(smooth?.shoulderRates),
      )
      : 0;
    const years = priced > 0 ? Math.min(nominal, priced) : nominal;
    return Array.from({ length: years }, (_, i) => `year_${i + 1}`);
  };

  // Render offer card component
  const renderOfferCard = (offer: Offer, contractMonths?: number) => {
    // First try to find retailer in database retailers, then fallback to hardcoded ones
    const retailer = retailers.find(r => r.id === offer.retailerId) ||
      ENERGY_RETAILERS.find(r => r.id === offer.retailerId);
    const isExpanded = expandedOffers.has(offer.id);

    // Get contract term to display (from param or first available/recommended)
    const displayTerm = contractMonths ||
      (offer.recommendedMonths && offer.recommendedMonths.length > 0 ? offer.recommendedMonths[0] : null) ||
      (offer.availableTerms && offer.availableTerms.length > 0 ? offer.availableTerms[0] : 12);

    // Get years to display based on contract term
    const yearsToDisplay = getYearsToDisplay(displayTerm, offer);

    return (
      <Card key={offer.id} className="transition-all duration-200 hover:shadow-md border-l-2 bg-white"
        style={{ borderLeftColor: (retailer as any)?.brandColors?.primary || '#3B82F6' }}>
        <CardHeader className="pb-2 px-3 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Retailer Logo */}
              <div
                className="w-8 h-8 rounded flex items-center justify-center bg-white border shadow-sm"
                style={{
                  backgroundColor: `#6B728010`,
                  borderColor: `#3B82F620`
                }}
              >
                {(retailer?.logo || offer.retailerLogo) ? (
                  <img
                    src={retailer?.logo || offer.retailerLogo}
                    alt={retailer?.name || offer.retailerName || 'Retailer'}
                    className="max-w-6 max-h-6 object-contain"
                  />
                ) : (
                  <div className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-medium bg-blue-600">
                    {(retailer?.name || offer.retailerName || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
              </div>

              <div>
                <CardTitle className="text-sm font-medium text-gray-900">
                  {retailer?.name || offer.retailerName || 'Unknown Retailer'}
                </CardTitle>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Calendar className="w-3 h-3 text-gray-400" />
                  <p className="text-xs text-gray-500 font-medium">
                    {formatRecommendedMonths(offer)}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                <Star className="w-3 h-3 mr-1" />
                Recommended
              </Badge>

              <Button
                variant="outline"
                size="sm"
                onClick={() => toggleOfferExpansion(offer.id)}
                className="ml-2 transition-all duration-200"
              >
                {isExpanded ? (
                  <>
                    <EyeOff className="w-4 h-4 mr-1" />
                    Hide Details
                    <ChevronUp className="w-4 h-4 ml-1" />
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-1" />
                    View Details
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>


          {/* Expandable Detailed Information */}
          <div className={`transition-all duration-500 ease-in-out overflow-hidden ${isExpanded ? 'max-h-[2000px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'
            }`}>
            {isExpanded && (
              <div className="animate-in slide-in-from-top-2 duration-300">
                {renderOfferDetails(offer, yearsToDisplay, displayTerm)}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Show loading skeleton while offers or retailers are being fetched,
  // or while waiting for sites to load (sites.length === 0 but tenderId exists)
  const isLoading = isLoadingOffers || loadingRetailers ||
    (offers.length === 0 && !!tenderId && sites.length === 0);

  if (isLoading) {
    return (
      <div className="w-full space-y-6">
        {/* Header skeleton */}
        <div className="flex items-center justify-between mb-6">
          <div className="space-y-2">
            <div className="h-5 w-56 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-72 bg-gray-100 rounded animate-pulse" />
          </div>
          <div className="flex items-center gap-3">
            <div className="h-5 w-16 bg-gray-100 rounded animate-pulse" />
            <div className="h-5 w-16 bg-gray-100 rounded animate-pulse" />
          </div>
        </div>

        {/* Contract term filter skeleton */}
        <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse" />
            <div className="space-y-1.5">
              <div className="h-5 w-44 bg-gray-200 rounded animate-pulse" />
              <div className="h-3.5 w-64 bg-gray-100 rounded animate-pulse" />
            </div>
          </div>
          <div className="h-12 w-64 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Offer card skeletons */}
        {[1, 2, 3].map(i => (
          <div key={i} className="border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            {/* Card header */}
            <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-gray-200 rounded animate-pulse" />
                <div className="space-y-1.5">
                  <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                  <div className="h-3 w-20 bg-gray-100 rounded animate-pulse" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-5 w-16 bg-gray-100 rounded animate-pulse" />
                <div className="h-5 w-20 bg-gray-100 rounded animate-pulse" />
                <div className="h-5 w-5 bg-gray-100 rounded animate-pulse" />
              </div>
            </div>
            {/* Card rows */}
            <div className="px-4 py-3 space-y-2">
              <div className="h-3 w-full bg-gray-100 rounded animate-pulse" />
              <div className="h-3 w-4/5 bg-gray-100 rounded animate-pulse" />
            </div>
          </div>
        ))}

        {/* Loading message */}
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-2">
          <svg className="animate-spin w-4 h-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading recommended offers...
        </div>

        <WorkflowNavigation
          onNext={onNext}
          onBack={onPrevious}
          isNextDisabled={true}
          nextButtonText="Continue to Templates"
          backButtonText="Back to Offers"
        />
      </div>
    );
  }

  if (!hasRecommendedOffers) {
    return (
      <div className="w-full space-y-6">
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Recommended Offers</h2>
          <p className="text-gray-600 mb-6">
            Please go back to Step 2 and select recommended contract lengths for your offers.
          </p>
        </div>

        {/* Navigation */}
        <WorkflowNavigation
          onNext={onNext}
          onBack={onPrevious}
          isNextDisabled={true}
          nextButtonText="Continue to Templates"
          backButtonText="Back to Offers"
        />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Recommended Energy Offers</h2>
          <p className="text-sm text-gray-600 mt-1">
            Review offers organized by site for {clientData.name}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Building className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-700">
              {getOffersBySite().length} sites
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="text-xs font-medium text-green-700">
              {offers.filter(o => o.recommendedMonths?.length > 0).length} offers
            </span>
          </div>
        </div>
      </div>

      {/* COMMON Contract Term Filter Dropdown - ONLY ONE DROPDOWN */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-6 shadow-lg mb-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-200 rounded-lg">
              <Calendar className="w-6 h-6 text-blue-700" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Filter by Contract Term</h3>
              <p className="text-sm text-gray-600 mt-1">Select a contract term to filter all offers</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1 max-w-sm">
              <Select
                value={selectedContractTerm?.toString() || 'all'}
                onValueChange={(value) => {
                  setSelectedContractTerm(value === 'all' ? null : parseInt(value));
                }}
              >
                <SelectTrigger className="w-full h-12 bg-white border-2 border-blue-400 hover:border-blue-500 transition-colors shadow-md text-base font-medium">
                  <SelectValue placeholder="All Contract Terms">
                    {selectedContractTerm ? formatTermLabel(selectedContractTerm) : 'All Contract Terms'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Contract Terms</SelectItem>
                  {getAvailableContractTerms().map((months) => (
                    <SelectItem key={months} value={months.toString()}>
                      {formatTermLabel(months)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedContractTerm && (
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-200 rounded-md border border-blue-300">
                <CheckCircle2 className="w-5 h-5 text-blue-700" />
                <span className="text-base font-semibold text-blue-800">
                  Showing {selectedContractTerm}-month offers only
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sites with Offers */}
      <div className="space-y-3">

        {(() => {
          const sitesWithOffers = getOffersBySite();

          if (sitesWithOffers.length === 0) {
            return (
              <Card className="border-dashed border-2 border-gray-300">
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <AlertCircle className="w-10 h-10 text-gray-400 mb-3" />
                  <h4 className="text-base font-medium text-gray-900 mb-2">
                    No offers configured
                  </h4>
                  <p className="text-sm text-gray-600 text-center max-w-md">
                    Go back to Step 2 and configure offers with recommended contract lengths.
                  </p>
                </CardContent>
              </Card>
            );
          }

          return sitesWithOffers.map(({ site }) => {
            const stats = getSiteStats(site.id);
            const isExpanded = expandedSites.has(site.id);

            return (
              <Card key={site.id} className="border border-gray-200 shadow-sm">
                <CardHeader
                  className="cursor-pointer hover:bg-gray-50 transition-colors py-3 px-4"
                  onClick={() => toggleSiteExpansion(site.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <Building className="w-4 h-4 text-blue-600" />
                      <div>
                        <CardTitle className="text-sm font-medium text-gray-900">
                          {site.name || 'Unnamed Site'}
                        </CardTitle>
                        <p className="text-xs text-gray-500 mt-0.5">
                          NMI: {site.nmi || 'Not specified'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs px-2 py-0.5">
                        {stats.totalOffers} offer{stats.totalOffers !== 1 ? 's' : ''}
                      </Badge>
                      <Badge variant="outline" className="text-xs px-2 py-0.5">
                        {stats.contractLengths.length} term{stats.contractLengths.length !== 1 ? 's' : ''}
                      </Badge>
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="pt-0 px-4 pb-4">
                    <div className="space-y-4">
                      {selectedContractTerm ? (
                        // When contract term is selected, show all offers with that term directly
                        (() => {
                          const siteOffers = getOffersBySite().find(({ site: s }) => s.id === site.id);
                          const filteredOffers = siteOffers?.offers.filter(offer => offer.recommendedMonths?.includes(selectedContractTerm) ?? false) || [];

                          if (filteredOffers.length === 0) {
                            return (
                              <div className="text-center py-4 text-gray-500">
                                <p className="text-sm">No offers found for {selectedContractTerm}-month contract term</p>
                              </div>
                            );
                          }

                          return (
                            <div className="border-l-2 border-blue-100 pl-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Calendar className="w-3.5 h-3.5 text-blue-600" />
                                <h4 className="text-sm font-medium text-gray-800">
                                  {formatTermLabel(selectedContractTerm)} Contract
                                </h4>
                                <Badge variant="outline" className="text-xs px-1.5 py-0.5">
                                  {filteredOffers.length}
                                </Badge>
                              </div>
                              <div className="grid gap-2">
                                {filteredOffers.map(offer => renderOfferCard(offer, selectedContractTerm))}
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        // When no contract term selected, show grouped by RECOMMENDED months only
                        getRecommendedContractTerms().map((months) => {
                          const siteOffersForMonth = getSiteOffersForMonth(site.id, months);

                          if (siteOffersForMonth.length === 0) return null;

                          return (
                            <div key={months} className="border-l-2 border-blue-100 pl-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Calendar className="w-3.5 h-3.5 text-blue-600" />
                                <h4 className="text-sm font-medium text-gray-800">
                                  {months} Month Contract
                                </h4>
                                <Badge variant="outline" className="text-xs px-1.5 py-0.5">
                                  {siteOffersForMonth.length}
                                </Badge>
                              </div>
                              <div className="grid gap-2">
                                {siteOffersForMonth.map(offer => renderOfferCard(offer, months))}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          });
        })()}
      </div>

      {/* Navigation */}
      <WorkflowNavigation
        onNext={onNext}
        onBack={onPrevious}
        isNextDisabled={!isReadyForNext()}
        nextButtonText="Continue to Templates"
        backButtonText="Back to Offers"
      />
    </div>
  );
};

export default RecommendedOfferStep;
